import React, { useState, useCallback } from "react";

import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { Link as RouterLink, useNavigate } from "react-router";

import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { ORGANIZATIONS } from "src/platform/constants/routes";
import { useRegistryApi } from "src/platform/hooks/useRegistryApi";
import debugLogger from "src/platform/utils/debugLogger";
import {
  getContainerStyles,
  getCardStyles,
  getButtonStyles,
  sectionLabelSx,
} from "src/shell/styles/themeUtils";

import type { Organization } from "../../types/OrganizationTypes";

const focusRingSx = (theme: Theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 1,
    transition: "all 150ms ease",
    "& fieldset": {
      borderColor: alpha(theme.palette.text.primary, 0.12),
    },
    "&:hover fieldset": {
      borderColor: alpha(theme.palette.primary.main, 0.5),
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: 1,
    },
    "&.Mui-focused": {
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
});

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const CreateOrganization = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [name, setName] = useState("");
  const [alias, setAlias] = useState("");
  const [aliasTouched, setAliasTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [nameError, setNameError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { call: createOrg } = useRegistryApi<Organization>(
    "organization",
    "createOrganization",
  );

  const autoAlias = slugify(name);
  const effectiveAlias = aliasTouched ? alias : autoAlias;

  const validateName = useCallback((value: string) => {
    if (!value.trim()) {
      setNameError("Organization name is required");
      return false;
    }
    if (value.length < 3) {
      setNameError("Organization name must be at least 3 characters");
      return false;
    }
    setNameError("");
    return true;
  }, []);

  const handleAddTag = useCallback(() => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagInput("");
    }
  }, [tagInput, tags]);

  const handleRemoveTag = useCallback(
    (tag: string) => {
      setTags(tags.filter((t) => t !== tag));
    },
    [tags],
  );

  const handleTagKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && tagInput.trim()) {
        e.preventDefault();
        handleAddTag();
      }
    },
    [tagInput, handleAddTag],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateName(name)) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const org = await createOrg({
        body: {
          name,
          alias: effectiveAlias,
          description: description || undefined,
          website: website || undefined,
          email: email || undefined,
          tags: tags.length > 0 ? tags : undefined,
        },
      });

      // Select the org to get a step-2 org-scoped token
      await orgSessionManager.selectOrg(org.alias);
      void navigate(ORGANIZATIONS.DETAILS.getPath(org.alias));
    } catch (error) {
      const msg =
        error instanceof Error
          ? error.message
          : "Failed to create organization";
      debugLogger.error("create-org", "Organization creation failed", msg);
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container sx={getContainerStyles(theme)}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: theme.spacing(2) }}
      >
        <Link
          color="inherit"
          component={RouterLink}
          to={ORGANIZATIONS.LIST.path}
          sx={{ textDecoration: "none" }}
        >
          Organizations
        </Link>
        <Typography color="text.primary">Create</Typography>
      </Breadcrumbs>

      <form onSubmit={handleSubmit}>
        <Stack spacing={4}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5" fontWeight={700}>
              Create Organization
            </Typography>
            <Button
              variant="outlined"
              component={RouterLink}
              to={ORGANIZATIONS.LIST.path}
              disabled={isSubmitting}
              sx={getButtonStyles(theme, "outlined")}
            >
              Cancel
            </Button>
          </Stack>

          {submitError && (
            <Alert severity="error" sx={{ borderRadius: 1 }}>
              {submitError}
            </Alert>
          )}

          {/* Basic Information */}
          <Card sx={getCardStyles(theme)}>
            <CardContent sx={{ p: 3 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 3 }}
              >
                <BusinessIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" sx={sectionLabelSx}>
                  Basic Information
                </Typography>
              </Stack>

              <Stack spacing={3}>
                <TextField
                  label="Organization Name"
                  required
                  fullWidth
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (nameError) validateName(e.target.value);
                  }}
                  onBlur={(e) => validateName(e.target.value)}
                  error={!!nameError}
                  helperText={nameError}
                  disabled={isSubmitting}
                  sx={focusRingSx(theme)}
                />

                <Box>
                  <TextField
                    label="URL Slug"
                    fullWidth
                    value={effectiveAlias}
                    onChange={(e) => {
                      setAliasTouched(true);
                      setAlias(slugify(e.target.value));
                    }}
                    disabled={isSubmitting}
                    helperText="Used in URLs, e.g. /organizations/my-org"
                    sx={focusRingSx(theme)}
                    InputProps={{
                      sx: { fontFamily: "monospace", fontSize: "0.875rem" },
                    }}
                  />
                  {effectiveAlias && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        ml: 1.5,
                        color: "text.secondary",
                        fontFamily: "monospace",
                      }}
                    >
                      Preview: /organizations/{effectiveAlias}
                    </Typography>
                  )}
                </Box>

                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of your organization"
                  disabled={isSubmitting}
                  sx={focusRingSx(theme)}
                />

                <TextField
                  label="Website"
                  fullWidth
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://example.com"
                  disabled={isSubmitting}
                  sx={focusRingSx(theme)}
                />

                <TextField
                  label="Contact Email"
                  fullWidth
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="contact@example.com"
                  disabled={isSubmitting}
                  sx={focusRingSx(theme)}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Tags */}
          <Card sx={getCardStyles(theme)}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 3 }}>
                Tags
              </Typography>

              <TextField
                label="Add tag"
                fullWidth
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="Type a tag and press Enter"
                disabled={isSubmitting}
                sx={focusRingSx(theme)}
                InputProps={{
                  endAdornment: tagInput.trim() ? (
                    <Button
                      size="small"
                      onClick={handleAddTag}
                      sx={{ minWidth: "auto", textTransform: "none" }}
                    >
                      Add
                    </Button>
                  ) : null,
                }}
              />

              {tags.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                  {tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      deleteIcon={<CloseIcon fontSize="small" />}
                      sx={{ borderRadius: 1.5 }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  No tags added yet. Tags help you organize and categorize
                  organizations.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Submit */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || !name.trim()}
              sx={{
                ...getButtonStyles(theme, "contained"),
                minWidth: { xs: "100%", sm: "200px" },
                "&:active": { transform: "scale(0.98)" },
              }}
            >
              {isSubmitting ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} color="inherit" />
                  <span>Creating...</span>
                </Stack>
              ) : (
                "Create Organization"
              )}
            </Button>
          </Box>
        </Stack>
      </form>
    </Container>
  );
};

export default CreateOrganization;
